import React from 'react';
import { graphql } from 'gatsby';
import parse from 'html-react-parser';
import Image from '@ecs/tng-common/components/image/Image';
import LayoutFull from '../../components/LayoutFull';
import CreditScoreSearch from '../../components/CreditScoreSearch';
import SEO from '../../components/SEO';
import TableOfContents from '../../components/posts/TableOfContents';
import {
  getMetaDescription,
  getCSrange,
  getTitle,
  getScoreRangeImage,
  scoreContentConfig,
  getScoreComponent,
  replaceScore,
  addDisclosureIconContent
} from '../../content/score-pages';
import { pathPrefix } from '../../helpers/routes';
import { setDataSection, setEcsSiteSection } from '../../helpers/analytics';
import { IPost } from '../../helpers/interfaces';
import TopAdvertiserDisclosure from '../../components/posts/TopAdvertiserDisclosure';
import StickyBannerDynamic from '../../components/StickyBannerDynamic';
import BlogCategoryLinks from '../../components/BlogCategoryLinks';
import BottomPromoBanner from '../../components/posts/BottomPromoBanner';
import { contWidthClases, setSeoMetaTags } from '../../helpers/functions';
import { scoreGuideLinks } from '../../content/blog-category-links';
import '../../styles/post-score-template.scss';
import SocialShare from '../../components/SocialShare';

interface IScoreTemplateProps {
  pageContext: {
    score: number;
    postId: number;
    authId: number;
  };
  data: {
    post: IPost;
  };
}

export default function ScoreTemplate({ pageContext, data }: IScoreTemplateProps) {
  const { score } = pageContext;
  const { categories } = data.post;
  const { databaseId: categoryId, slug: catSlug } = categories?.nodes[0] ?? {};
  const scoreRange = getCSrange(score);
  const configScore = getScoreComponent(score);
  const image = getScoreRangeImage(scoreRange);
  // content length 13
  const content = configScore ? scoreContentConfig[configScore] : null;
  const sections = [1, 2, 3, 4, 5];
  const contentTableOfContents = sections.map((section, index) => ({
    title: replaceScore(score, content[3 + index * 2]),
    id: `s${section}`
  }));

  return (
    <LayoutFull>
      <TopAdvertiserDisclosure />
      <div className="container container-sm-py">
        <article>
          <div className="row justify-content-xl-center">
            <div className="col-12 col-xl-11 col-xxl-10">
              <div className="row d-lg-block clearfix gx-lg-5 gx-xl-6 gx-xxl-7">
                <div className={`${contWidthClases} order-1`}>
                  <h1>{getTitle(score)}</h1>
                </div>
                {content ? (
                  <>
                    <TableOfContents
                      content={contentTableOfContents}
                      className="float-lg-end order-3 order-lg-2"
                    />
                    <div className={`${contWidthClases} order-4`}>
                      {addDisclosureIconContent(replaceScore(score, content[0]))}
                      <p className="text-center h4 mt-6 mt-lg-7 mb-4">
                        {parse(replaceScore(score, content[1]))}
                      </p>
                      <Image
                        src={image.img}
                        alt={image.alt}
                        width="470"
                        className="d-block mx-auto img-fluid content-mb"
                      />

                      {parse(content[2])}
                      {sections.map((section, index) => (
                        <section
                          className={`${index === 4 ? 'content-mb' : ''}`}
                          id={`s${section}`}
                          key={section}>
                          <h2>{parse(replaceScore(score, content[3 + index * 2]))}</h2>
                          {parse(replaceScore(score, content[4 + index * 2]))}
                        </section>
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </article>
        <div className="row justify-content-xl-center">
          <div className="col-12 col-xl-11 col-xxl-10">
            <div className="row">
              <div className={contWidthClases}>
                <CreditScoreSearch
                  variant="dark"
                  className="content-mb"
                />
                <BottomPromoBanner
                  className="content-mb"
                  category={catSlug}
                  categoryId={categoryId}
                />
                <BlogCategoryLinks links={scoreGuideLinks} />
                <section className="content-mt">
                  <h2 className="eyebrow-text">Share article</h2>
                  <SocialShare
                    title={getTitle(score)}
                    link={`${pathPrefix}${data.post?.uri}`} // This used canonical but no longer set by SEO
                  />
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
      <StickyBannerDynamic
        category={catSlug}
        categoryId={categoryId}
      />
    </LayoutFull>
  );
}
export function Head({ pageContext, data }: IScoreTemplateProps) {
  const { score, postId } = pageContext;
  const { expSeo: seo, categories } = data.post;
  const scoreRange = getCSrange(score);
  const title = `${getTitle(score)} - Experian`;
  const description = getMetaDescription(score, scoreRange);
  // const image = imageWithPrefix(getScoreRangeImage(scoreRange).img);
  const { databaseId: categoryId, slug: category } = categories?.nodes[0] ?? {};
  const section = setDataSection(category || '');
  return (
    <>
      <body
        data-section={section}
        data-category={category}
        data-categoryid={categoryId}
        data-id={postId}
        data-op="EXP-ASK-ART"
        data-ecscategory={setEcsSiteSection('score-guide', { category, section })}
      />
      <SEO
        title={title}
        description={description}
        noindex={seo?.metaRobotsNoindex !== 'index'}>
        {seo && setSeoMetaTags(seo)}
      </SEO>
    </>
  );
}
export const query = graphql`
  query ScoreSinglePost($postId: Int!) {
    post: wpPost(databaseId: { eq: $postId }) {
      categories {
        nodes {
          databaseId
          slug
        }
      }
      expSeo {
        ...ExpSEOFields
      }
    }
  }
`;
